import { createStackNavigator } from '@react-navigation/stack';
import { Module } from '../Routes';
import { AccessSchemas } from '../schemas/access';
import { AccessParamList } from '../typings/Params';

import {
    faBuildingShield,
    faTag,
    faTicket,
    faUserGroup,
    faLandmark,
    faBuilding,
    faWifi,
    faToolbox,
    faRecycle,
    faSitemap,
    faCog,
    faNetworkWired,
    faPersonHarassing,
    faStar,
} from '@fortawesome/free-solid-svg-icons';
import {
    faCameraCctv,
    faIdBadge,
    faCalendarCircleUser,
    faUserPolice,
    faCalendarStar,
} from '@fortawesome/pro-solid-svg-icons';

// access
import ActingBodies from '../pages/access/ActingBodies/ActingBodies';
import ActingBodiesForm from '../pages/access/ActingBodies/ActingBodyForm';
import ActingBodyUnits from '../pages/access/ActingBodyUnits/ActingBodyUnits';
import ActingBodyCommands from '../pages/access/ActingBodyCommands/ActingBodyCommands';
import Roles from '../pages/access/Roles/Roles';
import AllowedIps from '../pages/access/AllowedIps/AllowedIps';
import GroupForm from '../pages/access/Groups/GroupForm';
import Groups from '../pages/access/Groups/Groups';
import InstallationCompanies from '../pages/access/InstallationCompanies/InstallationCompanies';
import InternetCompanies from '../pages/access/InternetCompanies/InternetCompanies';
import AbuseReports from '../pages/access/AbuseReports/AbuseReports';
import PermissionLevelForm from '../pages/access/PermissionLevels/PermissionLevelForm';
import PermissionLevels from '../pages/access/PermissionLevels/PermissionLevels';
import PlatformSettings from '../pages/access/PlatformSettings/PlatformSettings';
import PtzPriority from '../pages/access/PtzPriority/PtzPriority';
import Turnstiles from '../pages/access/Turnstiles/Turnstiles';
import Users from '../pages/access/Users/Users';
import UserForm from '../pages/access/Users/UserForm';
import UserTags from '../pages/access/UserTags/UserTags';
import WorkSchedules from '../pages/access/WorkSchedules/WorkSchedules';
import Skills from '../pages/access/Skills/Skill';
import Holidays from '../pages/access/Holidays/Holidays';
import SupportTickets from '../pages/access/SupportTickets/SupportTickets';
import SupportTicketDetail from '../pages/access/SupportTickets/SupportTicketDetail';

export const accessModule: Module<'Access'> = {
    path: '/access',
    stackNavigator: createStackNavigator<AccessParamList>(),
    canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
    icon: require('../../assets/icon_access.png'),
    logo: require('../../assets/logo_access.png'),
    label: 'access',
    navigate: (navigation) => navigation.navigate('Access'),
    schemas: AccessSchemas,
    screens: {
        Users: {
            path: 'users',
            selectedItem: 'users',
            component: Users,
            label: 'users',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_user_settings
                || user?.permissions?.access_edit_user_acting_body_units
                || user?.permissions?.access_edit_user_all_acting_bodies
                || user?.permissions?.access_remove_user_acting_body
                || user?.permissions?.access_remove_user_acting_body_unit
                || user?.permissions?.access_remove_user_all_acting_bodies
                || user?.permissions?.access_edit_user_photo
                || user?.permissions?.access_edit_user_photo_acting_body_unit
                || user?.permissions?.access_edit_user_photo_all_acting_bodies
            ),
            accessInformation: {
                icon: faIdBadge,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'Users' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        UserForm: {
            path: 'users/form/:id?',
            selectedItem: 'users',
            component: UserForm,
            label: 'users',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_user_settings
                || user?.permissions?.access_edit_user_acting_body_units
                || user?.permissions?.access_edit_user_all_acting_bodies
                || user?.permissions?.access_remove_user_acting_body
                || user?.permissions?.access_remove_user_acting_body_unit
                || user?.permissions?.access_remove_user_all_acting_bodies
            ),
        },
        WorkSchedules: {
            path: 'work-schedules',
            selectedItem: 'work-schedules',
            component: WorkSchedules,
            label: 'workSchedules',
            canAccess: (user) => Boolean(
                user?.permissions?.access_manage_work_schedules
                || user?.permissions?.access_manage_all_acting_bodies_work_schedules
            ),
            accessInformation: {
                icon: faCalendarCircleUser,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'WorkSchedules' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        UserTags: {
            path: 'user-tags',
            selectedItem: 'user-tags',
            component: UserTags,
            label: 'userTags',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_management_user_tags
            ),
            accessInformation: {
                icon: faTag,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'UserTags' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        PermissionLevels: {
            path: 'permission-levels',
            selectedItem: 'permission-levels',
            component: PermissionLevels,
            label: 'permissionLevels',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_permission_level_settings
                || user?.permissions?.access_edit_permission_level_settings_acting_body_unit
                || user?.permissions?.access_edit_permission_level_settings_all_acting_bodies
            ),
            accessInformation: {
                icon: faUserGroup,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'PermissionLevels' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        PermissionLevelForm: {
            path: 'permission-levels/form/:id?',
            selectedItem: 'permission-levels',
            component: PermissionLevelForm,
            label: 'permissionLevels',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_permission_level_settings
                || user?.permissions?.access_edit_permission_level_settings_acting_body_unit
                || user?.permissions?.access_edit_permission_level_settings_all_acting_bodies
            ),
        },
        Groups: {
            path: 'groups',
            selectedItem: 'groups',
            component: Groups,
            label: 'groups',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_group_settings
                || user?.permissions?.access_edit_group_settings_acting_body_unit
                || user?.permissions?.access_edit_group_settings_all_acting_bodies
            ),
            accessInformation: {
                icon: faCameraCctv,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'Groups' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        GroupForm: {
            path: 'groups/form/:id?',
            selectedItem: 'groups',
            component: GroupForm,
            label: 'groups',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_group_settings
                || user?.permissions?.access_edit_group_settings_acting_body_unit
                || user?.permissions?.access_edit_group_settings_all_acting_bodies
            ),
        },
        ActingBodies: {
            path: 'acting-bodies',
            selectedItem: 'acting-bodies',
            component: ActingBodies,
            label: 'actingBodies',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_acting_body
            ),
            accessInformation: {
                icon: faLandmark,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'ActingBodies' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        ActingBodiesForm: {
            path: 'acting-bodies/form/:id?',
            selectedItem: 'acting-bodies',
            component: ActingBodiesForm,
            label: 'actingBodies',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_acting_body
            ),
        },
        ActingBodyUnits: {
            path: 'acting-body-units',
            selectedItem: 'acting-body-units',
            component: ActingBodyUnits,
            label: 'actingBodyUnits',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_acting_body
            ),
            accessInformation: {
                icon: faBuilding,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'ActingBodyUnits' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        ActingBodyCommands: {
            path: 'acting-body-commands',
            selectedItem: 'acting-body-commands',
            component: ActingBodyCommands,
            label: 'commands',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_acting_body
            ),
            accessInformation: {
                icon: faBuildingShield,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'ActingBodyCommands' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        Roles: {
            path: 'roles',
            selectedItem: 'roles',
            component: Roles,
            label: 'roles',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_acting_body
            ),
            accessInformation: {
                icon: faUserPolice,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'Roles' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        InstallationCompanies: {
            path: 'installation-companies',
            selectedItem: 'installation-companies',
            component: InstallationCompanies,
            label: 'installationCompanies',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_installation_company
            ),
            accessInformation: {
                icon: faToolbox,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'InstallationCompanies' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        InternetCompanies: {
            path: 'internet-companies',
            selectedItem: 'internet-companies',
            component: InternetCompanies,
            label: 'internetCompanies',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_internet_company
            ),
            accessInformation: {
                icon: faWifi,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'InternetCompanies' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        PtzPriority: {
            path: 'ptz-priority',
            selectedItem: 'ptz-priority',
            component: PtzPriority,
            label: 'ptzPriority',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_ptz_priorities
            ),
            accessInformation: {
                icon: faSitemap,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'PtzPriority' }),
            },
        },
        Turnstiles: {
            path: 'turnstiles',
            selectedItem: 'turnstiles',
            component: Turnstiles,
            label: 'turnstiles',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_turnstile
            ),
            accessInformation: {
                icon: faRecycle,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'Turnstiles' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        PlatformSettings: {
            path: 'platform-settings',
            selectedItem: 'platform-settings',
            component: PlatformSettings,
            label: 'platformSettings',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_edit_platform_settings
            ),
            accessInformation: {
                icon: faCog,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'PlatformSettings' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        AllowedIps: {
            path: 'allowed-ips',
            selectedItem: 'allowed-ips',
            component: AllowedIps,
            label: 'allowedIps',
            canAccess: (user) => Boolean(
                user?.isAdmin
                || user?.permissions?.access_manage_allowed_ips
            ),
            accessInformation: {
                icon: faNetworkWired,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'AllowedIps' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        AbuseReports: {
            path: 'abuse-reports',
            selectedItem: 'abuse-reports',
            component: AbuseReports,
            label: 'abuseReports',
            canAccess: (user) => Boolean(
                user?.permissions?.access_see_abuse_reports
                || user?.permissions?.access_manage_abuse_reports
            ),
            accessInformation: {
                icon: faPersonHarassing,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'AbuseReports' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        Holidays: {
            path: 'holidays',
            selectedItem: 'holidays',
            component: Holidays,
            label: 'holidays',
            canAccess: (user) => Boolean(
                user?.permissions?.access_manage_holidays
            ),
            accessInformation: {
                icon: faCalendarStar,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'Holidays' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        Skills: {
            path: 'skills',
            selectedItem: 'skills',
            component: Skills,
            label: 'skills',
            canAccess: (user) => Boolean(
                user?.permissions?.access_manage_skills
                || user?.permissions?.access_manage_all_acting_bodies_skills
            ),
            accessInformation: {
                icon: faStar,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'Skills' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        SupportTickets: {
            path: 'support-tickets',
            selectedItem: 'support-tickets',
            component: SupportTickets,
            label: 'supportTickets',
            canAccess: (user) => Boolean(
                user?.permissions?.manage_support_tickets
            ),
            accessInformation: {
                icon: faTicket,
                navigate: (navigation) => navigation.navigate('Access', { screen: 'SupportTickets' }),
            },
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
        },
        SupportTicketDetail: {
            path: 'support-tickets/:id',
            selectedItem: 'support-tickets',
            component: SupportTicketDetail,
            label: 'supportTickets',
            canAccess: (user) => Boolean(
                user?.permissions?.manage_support_tickets
            ),
        }
    }
};
